import { io, Socket } from 'socket.io-client';

const defaultConfig = {
  secure: true,
  transports: ['websocket'],
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  reconnectionAttempts: Infinity, // Unlimited reconnection attempts
  autoConnect: true, // Don't connect automatically
  forceNew: false, // Reuse existing connection
  timeout: 10000, // Connection timeout
  closeOnBeforeunload: false, // Don't close on page unload
  upgrade: false // Don't try to upgrade transport (prevents multiple connection attempts)
};

export interface SocketManagerConfig {
  endpoint: string;
  token: string;
  userType: 'employee' | 'client';
  config?: Partial<typeof defaultConfig>;
}

type EventHandler<T = unknown> = (data: T) => void;
type ConnectionState = 'connected' | 'disconnected' | 'connecting' | 'not-initialized';

/**
 * WebSocketManager - Manages Socket.IO connections with best practices
 *
 * Features:
 * - Token-based authentication via handshake.auth
 * - Automatic reconnection with exponential backoff
 * - Proper event handler management and cleanup
 * - Type-safe event handling
 * - Memory leak prevention
 * - Connection state management
 * - Error handling and logging
 */
class WebSocketManager {
  private endpoint: string | null = null;
  private token: string | null = null;
  private userType: 'employee' | 'client' | null = null;
  private config: Partial<typeof defaultConfig> | null = null;
  private socket: Socket | null = null;
  private eventHandlers: Map<string, Set<EventHandler>> = new Map();
  private connectionListenersSetup = false;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private isManualDisconnect = false; // Track if disconnect was intentional
  private reconnectAttemptCount = 0; // Track reconnect attempts for exponential backoff

  /**
   * Get available event types
   */
  getEventList() {
    return {
      USER_BRAND_CHAT: {},
      EMPLOYEE: {
        NOTIFICATION: {
          COUNT_UPDATED: 'employee:notification:count-update',
          NEW: 'employee:notification:new',
          UPDATE: 'employee:notification:update'
        },
        REQUEST_CREATOR_ADDED: 'employee:request:creator-added',
        CREATOR_UPDATED: 'employee:creator:updated',
        AGENCY_UPDATED: 'employee:agency:updated',
      },
      INSIGHTS: {
        REFRESH_STATUS: 'insights:refresh-status'
      }
    };
  }

  /**
   * Initialize socket connection with authentication
   * Best practice: Use auth object for token-based authentication
   */
  initialize({ endpoint, token, userType, config }: SocketManagerConfig): void {
    // Prevent re-initialization if already connected
    if (this.socket?.connected) {
      console.warn('Socket already connected. Use reinitialize() to reset connection.');
      return;
    }

    // Clean up existing connection if any
    if (this.socket) {
      this.clean();
    }

    this.endpoint = endpoint;
    this.token = token;
    this.userType = userType;
    this.config = { ...defaultConfig, ...config };

    // Build socket options with authentication
    const socketOptions = {
      ...this.config,
      auth: {
        token: token,
        userType: userType
      }
    };

    // Create socket instance
    this.socket = io(endpoint, socketOptions);

    // Setup connection lifecycle listeners
    this.setupConnectionListeners();

    this.connect();
  }

  /**
   * Setup connection lifecycle listeners
   * Best practice: Handle all connection states properly
   */
  setupConnectionListeners(): void {
    if (!this.socket || this.connectionListenersSetup) {
      return;
    }

    this.connectionListenersSetup = true;

    // Connection established
    this.socket.on('connect', () => {
      console.log('[Socket] Connected successfully. Socket ID:', this.socket?.id);

      // Clear any pending reconnect timeouts since we're now connected
      if (this.reconnectTimeoutId) {
        clearTimeout(this.reconnectTimeoutId);
        this.reconnectTimeoutId = null;
      }

      // Reset flags on successful connection
      this.isManualDisconnect = false;
      this.reconnectAttemptCount = 0;

      this.emitToHandlers('connect', { connected: true, socketId: this.socket?.id });
    });

    // Connection error
    this.socket.on('connect_error', (error: Error) => {
      console.error('Socket connection error:', error);
      this.emitToHandlers('connect_error', error);
    });

    // Disconnected
    this.socket.on('disconnect', (reason: string) => {
      console.log('Socket disconnected:', reason);
      this.emitToHandlers('disconnect', { reason });

      // Auto-reconnect on unexpected disconnects (not manual disconnect)
      // Socket.IO's 'io client disconnect' reason indicates manual disconnect
      if (!this.isManualDisconnect && reason !== 'io client disconnect') {
        this.handleAutoReconnect(reason);
      }
      // Note: isManualDisconnect flag is reset on successful reconnection or in clean()
    });

    // Reconnection attempt
    this.socket.on('reconnect_attempt', (attemptNumber: number) => {
      console.log('Socket reconnection attempt:', attemptNumber);
      this.emitToHandlers('reconnect_attempt', { attemptNumber });
    });

    // Reconnection successful
    this.socket.on('reconnect', (attemptNumber: number) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');

      // Clear any pending reconnect timeouts since we're now connected
      if (this.reconnectTimeoutId) {
        clearTimeout(this.reconnectTimeoutId);
        this.reconnectTimeoutId = null;
      }

      // Reset reconnect attempt count on successful reconnection
      this.reconnectAttemptCount = 0;

      this.emitToHandlers('reconnect', { attemptNumber });
    });

    // Reconnection failed
    this.socket.on('reconnect_failed', () => {
      console.error('Socket reconnection failed - will retry automatically');
      this.emitToHandlers('reconnect_failed', {});

      // Continue trying to reconnect even after failure
      if (!this.isManualDisconnect) {
        this.handleAutoReconnect('reconnect_failed');
      }
    });

    // General error handler
    this.socket.on('error', (error: Error) => {
      console.error('Socket error:', error);
      this.emitToHandlers('error', error);
    });
  }

  /**
   * Connect to the server
   * Best practice: Check state before connecting
   */
  connect(): void {
    if (!this.socket) {
      throw new Error('Socket not initialized. Call initialize() first.');
    }

    if (this.socket.connected) {
      console.warn('[Socket] Already connected');
      return;
    }

    if (!this.socket.active) {
      this.socket.connect();
    } else {
      console.log('[Socket] Connection already in progress');
    }
  }

  /**
   * Disconnect from the server
   * Best practice: Graceful disconnection
   */
  disconnect(): void {
    // Mark as manual disconnect to prevent auto-reconnect
    this.isManualDisconnect = true;

    // Clear any pending reconnect timeouts
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.socket) {
      this.socket.disconnect();
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Register an event listener
   * Best practice: Return unsubscribe function for cleanup
   * Supports multiple handlers per event
   */
  on<T = unknown>(event: string, handler: EventHandler<T>): () => void {
    if (!this.socket) {
      console.error('Socket not initialized. Call initialize() first.');
      return () => null;
    }

    // Initialize handler set for this event if it doesn't exist
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());

      // Register with socket.io (only once per event type)
      this.socket.on(event, (data: T) => {
        this.emitToHandlers(event, data);
      });
    }

    // Add handler to the set
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.add(handler as EventHandler<unknown>);
    }

    // Return unsubscribe function for cleanup
    return () => {
      this.off(event, handler as EventHandler<unknown>);
    };
  }

  /**
   * Remove an event listener
   * Best practice: Support removing specific handler or all handlers for an event
   */
  off(event: string, handler?: EventHandler): void {
    if (!this.socket) {
      return;
    }

    if (handler) {
      // Remove specific handler
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        handlers.delete(handler);

        // If no more handlers, remove from socket and cleanup
        if (handlers.size === 0) {
          this.eventHandlers.delete(event);
          this.socket.off(event);
        }
      }
    } else {
      // Remove all handlers for this event
      this.eventHandlers.delete(event);
      this.socket.off(event);
    }
  }

  /**
   * Emit an event to the server
   * Best practice: Validate connection before emitting
   */
  emit(event: string, data?: unknown): void {
    if (!this.socket) {
      throw new Error('Socket not initialized. Call initialize() first.');
    }

    if (!this.socket.connected) {
      console.warn('Socket not connected. Event not sent:', event);
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Emit to all registered handlers for an event
   * Best practice: Wrap in try-catch to prevent one handler from breaking others
   */
  private emitToHandlers(event: string, data: unknown): void {
    const handlers = this.eventHandlers.get(event);

    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in socket event handler for "${event}":`, error);
        }
      });
    }
  }

  /**
   * Get the underlying socket instance
   * Use with caution - for advanced usage only
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    if (!this.socket) {
      return 'not-initialized';
    }
    if (this.socket.connected) {
      return 'connected';
    }
    if (this.socket.active) {
      return 'connecting';
    }
    return 'disconnected';
  }

  /**
   * Reinitialize socket connection
   * Best practice: Useful for token refresh or reconnection after errors
   */
  reinitialize(config?: SocketManagerConfig): void {
    // Clean up existing connection
    if (this.socket) {
      // Remove all socket listeners
      this.eventHandlers.forEach((_, event) => {
        this.socket?.off(event);
      });

      // Disconnect
      this.socket.disconnect();
      this.socket = null;
    }

    // Reset connection listeners flag
    this.connectionListenersSetup = false;

    // Clear event handlers map (but keep the structure)
    this.eventHandlers.clear();

    // Reinitialize with new or existing config
    if (config) {
      this.initialize(config);
    } else if (this.endpoint && this.token && this.userType) {
      this.initialize({
        endpoint: this.endpoint,
        token: this.token,
        userType: this.userType,
        config: this.config || undefined
      });
    }
  }

  /**
   * Handle automatic reconnection with exponential backoff
   * Continues attempting to reconnect until successful or manual disconnect
   */
  private handleAutoReconnect(reason: string): void {
    // Clear any existing reconnect timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Don't reconnect if socket is already connected or connecting
    if (this.socket?.connected || this.socket?.active) {
      return;
    }

    // Don't reconnect if it was a manual disconnect
    if (this.isManualDisconnect) {
      return;
    }

    // Don't reconnect if socket is not initialized
    if (!this.socket || !this.endpoint || !this.token || !this.userType) {
      return;
    }

    console.log('[Socket] Attempting automatic reconnection...');

    // Use exponential backoff: start with 2 seconds, max 30 seconds
    // Increase delay with each attempt: 2s, 3s, 4.5s, 6.75s, etc., capped at 30s
    const delay = Math.min(2000 * Math.pow(1.5, this.reconnectAttemptCount), 30000);
    this.reconnectAttemptCount++;

    console.log(`[Socket] Will reconnect in ${delay}ms (attempt ${this.reconnectAttemptCount})`);

    this.reconnectTimeoutId = setTimeout(() => {
      if (!this.isManualDisconnect && this.socket && !this.socket.connected) {
        console.log('[Socket] Reconnecting...');
        this.socket.connect();
      }
      this.reconnectTimeoutId = null;
    }, delay);
  }

  /**
   * Complete cleanup - removes all handlers and disconnects
   * Best practice: Call this when component unmounts or app closes
   */
  clean(): void {
    // Mark as manual disconnect to prevent auto-reconnect
    this.isManualDisconnect = true;

    // Clear any pending reconnect timeouts
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    if (this.socket) {
      // Remove all event listeners from socket
      this.eventHandlers.forEach((_, event) => {
        this.socket?.off(event);
      });

      // Disconnect socket
      this.socket.disconnect();
      this.socket = null;
    }

    // Clear all handlers
    this.eventHandlers.clear();

    // Reset state
    this.endpoint = null;
    this.token = null;
    this.userType = null;
    this.config = null;
    this.connectionListenersSetup = false;
    this.isManualDisconnect = false;
    this.reconnectAttemptCount = 0;
  }
}

// Export singleton instance for convenience
const socketManager = new WebSocketManager();

export { socketManager as WebSocketService };
